import { useEffect, useMemo, useState } from "react";
import { WorkspaceMembersCard } from "./WorkspaceMembersCard";
import { LibraryIsolationCard } from "./LibraryIsolationCard";
import { WorkspaceBrandHandleCard } from "./WorkspaceBrandHandleCard";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace, type Workspace } from "@/hooks/useWorkspace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ReadwiseIntegrationCard } from "@/components/settings/ReadwiseIntegrationCard";
import { WorkspaceMcpCard } from "@/components/settings/McpAccessCard";

import { WorkspaceExportCard } from "@/components/settings/WorkspaceExportCard";
import { CitationSettingsCard } from "@/components/settings/CitationSettingsCard";
import { AmazonAffiliateSettingsCard } from "@/components/settings/AmazonAffiliateSettingsCard";
import { AmazonPaapiSettingsCard } from "@/components/settings/AmazonPaapiSettingsCard";
import { ImageProviderSettingsCard } from "@/components/settings/ImageProviderSettingsCard";
import { AiProviderSettingsCard } from "@/components/settings/AiProviderSettingsCard";
import { KnowledgeWorkerCodexCard } from "@/components/settings/KnowledgeWorkerCodexCard";
import { ConnectorManager } from "@/components/composer/ConnectorManager";
import { AutomationHooksCard } from "@/components/settings/AutomationHooksCard";
import { ManualDestinationsCard } from "@/components/settings/ManualDestinationsCard";
import { WorkspaceDangerZoneCard } from "@/components/settings/WorkspaceDangerZoneCard";
import { tierEntitlements, tierDisplayName, tierHasCapability } from "@/lib/tier";
import { listConnectors, type ContentPublishingConnector } from "@/lib/composerCore";

import { toast } from "sonner";

type Props = {
  workspace: Workspace;
};

/**
 * Settings panel scoped to a single workspace. Split into four sub-tabs
 * (Workspace, Codex, Functions, Manage APIs) to keep the per-workspace
 * surface scannable. Mounted only when the parent workspace tab is active.
 */
export const WorkspaceSettingsTab = ({ workspace }: Props) => {
  const { user } = useAuth();
  const { refresh, workspaces } = useWorkspace();

  const [label, setLabel] = useState(workspace.workspace_label ?? "");
  const [savingLabel, setSavingLabel] = useState(false);
  const [subTab, setSubTab] = useState<"workspace" | "codex" | "functions" | "apis">("workspace");

  const canManageConnectors = tierHasCapability(workspace.account_tier ?? null, "content_publishing_dispatch");
  const [connectors, setConnectors] = useState<ContentPublishingConnector[]>([]);
  const [connectorsLoading, setConnectorsLoading] = useState(false);

  useEffect(() => {
    if (!canManageConnectors) {
      setConnectors([]);
      return;
    }
    let cancelled = false;
    setConnectorsLoading(true);
    void listConnectors(workspace.id)
      .then((rows) => { if (!cancelled) setConnectors(rows); })
      .catch((err) => { if (!cancelled) toast.error((err as Error).message); })
      .finally(() => { if (!cancelled) setConnectorsLoading(false); });
    return () => { cancelled = true; };
  }, [workspace.id, canManageConnectors]);

  const ent = tierEntitlements(workspace.account_tier);
  void ent;

  const expectedConfirmation = (workspace.workspace_label?.trim() || "this workspace");

  const primaryWorkspace = useMemo(() => workspaces.find((w) => w.is_primary), [workspaces]);
  const isPlanOwner = !!user && !!primaryWorkspace && primaryWorkspace.owner_user_id === user.id;
  const canHardDelete = isPlanOwner && !workspace.is_primary;

  useEffect(() => {
    setLabel(workspace.workspace_label ?? "");
  }, [workspace.id, workspace.workspace_label]);

  const saveLabel = async () => {
    const trimmed = label.trim();
    if (!trimmed) return toast.error("Workspace name can't be empty");
    setSavingLabel(true);
    const { error } = await supabase
      .from("profiles")
      .update({ workspace_label: trimmed })
      .eq("id", workspace.id);
    setSavingLabel(false);
    if (error) return toast.error(error.message);
    toast.success("Workspace renamed");
    refresh();
  };

  return (
    <Tabs value={subTab} onValueChange={(v) => setSubTab(v as typeof subTab)} className="space-y-6">
      <TabsList className="flex w-full flex-wrap justify-start gap-1 overflow-x-auto">
        <TabsTrigger value="workspace">Workspace</TabsTrigger>
        <TabsTrigger value="codex">Codex</TabsTrigger>
        <TabsTrigger value="functions">Functions</TabsTrigger>
        <TabsTrigger value="apis">Manage APIs</TabsTrigger>
      </TabsList>

      <TabsContent value="workspace" className="space-y-6">
        <WorkspaceMembersCard workspace={workspace} />
        <Card className="shadow-soft">
          <CardHeader><CardTitle>Workspace name</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <Badge variant="secondary">{tierDisplayName(workspace.account_tier)}</Badge>
              <span className="text-sm text-muted-foreground truncate min-w-0">{user?.email}</span>
            </div>
            <div>
              <Label htmlFor={`ws-label-${workspace.id}`}>Workspace name</Label>
              <Input
                id={`ws-label-${workspace.id}`}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Newsletter, Client work, Research"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                This is just a label for this workspace tab. It's separate from your account display name and @handle.
              </p>
            </div>
            <div>
              <Button onClick={saveLabel} disabled={savingLabel}>
                {savingLabel ? "Saving…" : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <WorkspaceBrandHandleCard workspace={workspace} />

        <LibraryIsolationCard workspace={workspace} />

        <WorkspaceExportCard workspace={workspace} />

        <WorkspaceDangerZoneCard
          workspace={workspace}
          expectedConfirmation={expectedConfirmation}
          canHardDelete={canHardDelete}
        />
      </TabsContent>

      <TabsContent value="codex" className="space-y-6">
        <KnowledgeWorkerCodexCard workspace={workspace} />

        {workspace.codex_enabled !== false && (
          <p className="text-sm text-muted-foreground">
            Edit your voice profile, audience avatars, and topic framework in Composer → Codex. The Master
            Voice Document is the single always-on memory the AI reads for this workspace.
          </p>
        )}
      </TabsContent>

      <TabsContent value="functions" className="space-y-6">
        <ManualDestinationsCard workspace={workspace} />
        <CitationSettingsCard workspace={workspace} />
      </TabsContent>

      <TabsContent value="apis" className="space-y-6">
        {!canManageConnectors ? (
          <Card className="shadow-soft">
            <CardContent className="pt-6 text-sm text-muted-foreground">
              Publishing connectors aren't included on the {tierDisplayName(workspace.account_tier ?? null)} plan. Upgrade to send prepared publications to email, Kit, Bento, and webhook destinations.
            </CardContent>
          </Card>
        ) : connectorsLoading ? (
          <Card className="shadow-soft">
            <CardContent className="pt-6 text-sm text-muted-foreground">Loading connectors…</CardContent>
          </Card>
        ) : (
          <ConnectorManager
            workspaceId={workspace.id}
            initialConnectors={connectors}
            canManage={canManageConnectors}
            onChange={setConnectors}
          />
        )}

        <WorkspaceMcpCard workspace={workspace} />

        <AutomationHooksCard workspaceId={workspace.id} />

        <ReadwiseIntegrationCard />

        <AmazonAffiliateSettingsCard workspace={workspace} />

        <AmazonPaapiSettingsCard workspace={workspace} />

        <AiProviderSettingsCard workspace={workspace} />

        <ImageProviderSettingsCard workspace={workspace} />
      </TabsContent>
    </Tabs>
  );
};
